import { type FormModel, IkeModel } from '@ikegps/core-shared';
import { AppStore } from '../store';
import { type IkeClientService, Services } from './index';

export class FormService implements IkeClientService {
	__priority = 100;

	/**
	 * Saves a form.
	 *
	 * @returns New, saved, form.
	 */
	async save(form: FormModel): Promise<FormModel> {
		const rest = Services.Department.rest;
		const formJSON = IkeModel.Form.write(form);

		const newFormJSON = await rest.save(rest.Objects.Form, formJSON);
		const savedForm = IkeModel.Form.read(newFormJSON);

		// Saving a form creates a new version document in the database, which gets a fresh _created_at.
		// Preserve the original createdAt for existing forms so the UI shows when the form was first created.
		if (form.id != null && form.createdAt != null) {
			savedForm.createdAt = form.createdAt;
		}

		return savedForm;
	}

	/**
	 * Archives a form, hiding it from active form lists.
	 *
	 * @returns Revision of archived form.
	 */
	async archive(uuid: string): Promise<string> {
		const rest = Services.Department.rest;

		const startTime = Date.now();
		const result = await rest.Form.delete({ uuid });
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Form,
			'FORM_ARCHIVE',
			`uuid: ${uuid}`,
			Date.now() - startTime
		);

		AppStore.FormSummary.refresh();

		return result.results.revision;
	}

	/**
	 * Unarchives a form, restoring it to the active form list.
	 *
	 * @returns Revision of restored form.
	 */
	async unarchive(uuid: string): Promise<string> {
		const rest = Services.Department.rest;

		const startTime = Date.now();
		const result = await rest.Form.restore({ uuid });
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Form,
			'FORM_UNARCHIVE',
			`uuid: ${uuid}`,
			Date.now() - startTime
		);

		AppStore.FormSummary.refresh();

		return result.results.revision;
	}

	/**
	 * Shares a form with another department of the same organization.
	 *
	 * @returns New form revision.
	 */
	async share(revision: string, toDepartmentId: string): Promise<string> {
		const rest = Services.Department.rest;

		const startTime = Date.now();
		const result = await rest.Form.share({ revision, toDepartmentId });
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Form,
			'FORM_SHARE',
			`revision:${revision} -> department:${toDepartmentId}`,
			Date.now() - startTime
		);

		return result.results.revision;
	}

	async copy(formId: string, toDepartmentId?: string): Promise<string> {
		const rest = Services.Department.rest;

		const startTime = Date.now();
		const result = await rest.Form.copy({ formId, toDepartmentId });
		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Form,
			'FORM_COPY',
			toDepartmentId ? `id:${formId} -> department:${toDepartmentId}` : `id:${formId}`,
			Date.now() - startTime
		);

		return result.results.formId;
	}
}
